import { useDeviceType } from '@cian/ui-kit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { isAllowedCatProfilingSubdomain, useAllowedCatProfilingPage } from '../../hooks/useAllowedCatProfilingPage';
import { useIsBannerSkipped } from '../../hooks/useIsBannerSkipped';
import { selectOnboardingCookieState } from '../../selectors/cookies/selectOnboardingCookieState';
import { useApplicationContext } from '../../utils/applicationContext';

const DENIED_ONBOARDING_PAGES: Record<string, RegExp> = {
  OFFER_CARD_PAGE: /^\/(rent|sale)\/(flat|commercial|suburban)\/(\d+)\/?/,
};

export function useIsOnboardingEnabled(): boolean {
  const {
    config,
    custom: { originalSubdomain },
  } = useApplicationContext();
  const hasOnboardingCookie = useSelector(selectOnboardingCookieState);
  const deviceType = useDeviceType();
  const skippedBanner = useIsBannerSkipped();
  const isWebView = config.get<boolean>('web.isWebView') || false;

  const catProfilingOnboardingCompleted = useSelector(selectOnboardingCookieState);

  const shouldShowCatProfilingBanner = useMemo(() => {
    const profilingBannerEnabled = config.get<boolean>('BX.CatProfiling.Enabled') ?? false;

    return profilingBannerEnabled && !catProfilingOnboardingCompleted;
  }, [config, catProfilingOnboardingCompleted]);

  // переиспользуем хук, т.к. он проверяет разрешенные страницы для профилирования
  // TODO: доработать после раскатки/выпиливания профилирования
  const allowedPage = useAllowedCatProfilingPage();

  // Хук выше переиспользуется, но некоторые страницы могут быть запрещены для онбординга
  const isDeniedPage = useMemo(() => {
    if (!process.env.IS_BROWSER_BUILD) {
      return false;
    }

    return Object.values(DENIED_ONBOARDING_PAGES).some(page => page.test(location.pathname));
  }, []);

  const isDisabledOnDesktop = useMemo(
    () => deviceType === 'desktop' && shouldShowCatProfilingBanner,
    [deviceType, shouldShowCatProfilingBanner],
  );

  const isDisabledOnMobile = useMemo(
    () => deviceType === 'phone' && shouldShowCatProfilingBanner,
    [deviceType, shouldShowCatProfilingBanner],
  );

  const isDisabled = useMemo(
    () => skippedBanner || isWebView || isDisabledOnDesktop || isDisabledOnMobile,
    [isDisabledOnDesktop, isDisabledOnMobile, isWebView, skippedBanner],
  );

  const isAllowedOnboardingPage =
    isAllowedCatProfilingSubdomain(originalSubdomain) && (deviceType === 'phone' || (allowedPage && !isDeniedPage));

  return useMemo(
    () =>
      (config.get<boolean>('BX.WebOnboarding.Enabled') ?? false) &&
      !isDisabled &&
      isAllowedOnboardingPage &&
      !hasOnboardingCookie,
    [config, hasOnboardingCookie, isAllowedOnboardingPage, isDisabled],
  );
}
